import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { environment } from 'src/environments/environment'

/**
 * Project-level facts and the billing/subscription view of a project — the
 * reads that aren't owned by any one feature. Backed by stacks-agents
 * `src/app/api/account/` and `src/app/api/billing/`.
 */
@Injectable({ providedIn: 'root' })
export class ProjectApiService {
  constructor(private http: HttpClient) {}

  /** Builder JWT for the stacks-agents API */
  private agentsAuthHeaders(): HttpHeaders {
    return new HttpHeaders({ Authorization: `Bearer ${localStorage.getItem('token')}` })
  }

  getProjectMetadataValue(projectId: string, key: string): Promise<string | null> {
    return this.http
      .get<{ data?: string }>(`${environment.agentsUrl}v4/retrieval/project/metadata`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId, key },
      })
      .pipe(
        map(body => body?.data ?? null),
        catchError(() => of(null)),
      )
      .toPromise() as Promise<string | null>
  }

  getProjectSubscription(projectId: string): Promise<{ package: string; unlimitedSources: string[] } | null> {
    return this.http
      .get<{ data?: { package: string; unlimitedSources: string[] } }>(`${environment.agentsUrl}v4/retrieval/subscription`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId },
      })
      .pipe(
        map(body => body?.data ?? null),
        catchError(() => of(null)),
      )
      .toPromise() as Promise<{ package: string; unlimitedSources: string[] } | null>
  }

  getAddonSubscriptions(projectId: string): Promise<any[]> {
    return this.http
      .get<{ data?: any[] }>(`${environment.agentsUrl}v4/retrieval/subscription/addons`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId },
      })
      .pipe(
        map(body => body?.data ?? []),
        catchError(() => of([])),
      )
      .toPromise() as Promise<any[]>
  }

  /** Image URLs the builder must never delete (shared stock assets). */
  getProtectedImages(): Promise<string[]> {
    return this.http
      .get<{ data?: string[] }>(`${environment.agentsUrl}v4/retrieval/protected-images`, {
        headers: this.agentsAuthHeaders(),
      })
      .pipe(
        map(body => body?.data ?? []),
        catchError(() => of([])),
      )
      .toPromise() as Promise<string[]>
  }
}
